import { useCallback, useEffect, useMemo, useState } from 'react'
import { defaultVisibleColumnIds } from '@/config/columns'

function loadStored(storageKey, columns) {
  const validIds = new Set(columns.map((c) => c.id))
  const fallback = defaultVisibleColumnIds(columns)
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return fallback
    const filtered = parsed.filter((id) => validIds.has(id))
    return filtered.length ? filtered : fallback
  } catch {
    return fallback
  }
}

/** Columns in the order of `visibleIds` (table display order). */
export function columnsInVisibleOrder(columns, visibleIds) {
  const byId = Object.fromEntries(columns.map((col) => [col.id, col]))
  return visibleIds.map((id) => byId[id]).filter(Boolean)
}

export function useColumnVisibility(storageKey, columns) {
  const [visibleIds, setVisibleIds] = useState(() => loadStored(storageKey, columns))

  useEffect(() => {
    setVisibleIds(loadStored(storageKey, columns))
  }, [storageKey, columns])

  useEffect(() => {
    if (columns.length) {
      localStorage.setItem(storageKey, JSON.stringify(visibleIds))
    }
  }, [storageKey, visibleIds, columns.length])

  const visibleColumns = useMemo(
    () => columnsInVisibleOrder(columns, visibleIds),
    [columns, visibleIds],
  )

  const toggleColumn = useCallback((id) => {
    setVisibleIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev
        return prev.filter((x) => x !== id)
      }
      // Append so existing custom order is preserved.
      return [...prev, id]
    })
  }, [])

  const reorderVisible = useCallback((fromIndex, toIndex) => {
    setVisibleIds((prev) => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length ||
        fromIndex === toIndex
      ) {
        return prev
      }
      const next = [...prev]
      const [item] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, item)
      return next
    })
  }, [])

  const showAll = useCallback(() => {
    setVisibleIds(columns.map((c) => c.id))
  }, [columns])

  const resetDefaults = useCallback(() => {
    setVisibleIds(defaultVisibleColumnIds(columns))
  }, [columns])

  return {
    visibleIds,
    visibleColumns,
    toggleColumn,
    reorderVisible,
    showAll,
    resetDefaults,
  }
}
